// Auth state for the whole app. Creators sign in with Google (primary) or an
// email magic-link / 6-digit code (backup); caretakers get an anonymous session
// via the join flow. Either way a Supabase session lands here and every guarded
// route reads it through useAuth().

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "./supabase";

type AuthValue = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signInWithGoogle: (redirectTo?: string) => Promise<{ error: string | null }>;
  signInWithEmail: (email: string) => Promise<{ error: string | null }>;
  verifyEmailOtp: (email: string, token: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
};

const AuthCtx = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  async function signInWithGoogle(redirectTo?: string) {
    // Google OAuth round-trips through Supabase. Default lands at /babyhelper/;
    // Join passes /babyhelper/join/<token> so the invite token survives the
    // redirect and the join page can auto-claim once a session is present.
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: redirectTo ?? `${window.location.origin}/babyhelper/` },
    });
    return { error: error?.message ?? null };
  }

  async function signInWithEmail(email: string) {
    // Sends an email carrying both a magic link and a 6-digit code. The code
    // path (verifyEmailOtp) is what works inside an installed PWA, where the
    // link would otherwise open in the browser's separate storage.
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/babyhelper` },
    });
    return { error: error?.message ?? null };
  }

  async function verifyEmailOtp(email: string, token: string) {
    const { error } = await supabase.auth.verifyOtp({ email, token, type: "email" });
    return { error: error?.message ?? null };
  }

  async function signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }

  return (
    <AuthCtx.Provider
      value={{ session, user: session?.user ?? null, loading, signInWithGoogle, signInWithEmail, verifyEmailOtp, signOut }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthValue {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be used within AuthProvider");
  return v;
}
